import { frameId } from "../assets/manifest.ts";
import { playersAbove } from "../document/band.ts";
import type { MapDocument, PropInstance } from "../document/types.ts";
import { layerDepth } from "../editor/sort.ts";
import { holdsStill } from "../editor/stand-driven.ts";
import type { AppearanceTable } from "./behaviour.ts";
import { presenceIndex } from "./chunk.ts";
import type { CompileContext } from "./context.ts";
import { withBareFrames } from "./stand-own.ts";
import { ownMarksOf, standMarks, standPropDepth, type StandFrame, type StandPlacement, type StandTable } from "./stand-marks.ts";

/**
 * A painted frame's Behind and In front cells and the cell size they are counted in (ADR-132 decision 2), as the
 * compile context carries them by frame id.
 */
export interface FrameMarks {
  readonly cell: number;
  readonly behind: readonly (readonly [number, number])[];
  readonly front: readonly (readonly [number, number])[];
}

/**
 * The frames a context paints, each with the size and base row its marks are laid by: the base the frame draws with
 * (`drawn`, else the context's `frameSizes`, else the frame's bottom row), never the footprint's, which only places
 * its blocked cells. A frame the context cannot size is left out, and so is a ground frame, which draws flat under
 * characters by its own rule (ADR-040's amendment of 2026-10-06), so no depth baked beside a mark could put a
 * character behind it.
 *
 * @param context The compile context.
 * @param drawn What the frame is drawn with, where the caller holds it: the editor's atlas rect.
 * @returns A lookup from frame id to its marks and size.
 */
export function framesOf(context: Pick<CompileContext, "collision" | "frameSizes" | "standMarks">, drawn?: (frame: string) => { readonly base: number; readonly ground?: true } | undefined): (frame: string) => StandFrame | undefined {
  const painted = context.standMarks;
  const cache = new Map<string, StandFrame | undefined>();
  return (frame) => {
    if (cache.has(frame)) {
      return cache.get(frame);
    }
    const marks = painted?.[frame];
    const size = context.frameSizes?.[frame];
    const footprint = context.collision.footprints?.[frame];
    const w = footprint?.w ?? size?.w;
    const h = footprint?.h ?? size?.h;
    const draws = drawn?.(frame) ?? size;
    const found = marks === undefined || w === undefined || h === undefined || draws?.ground === true ? undefined : { w, h, base: draws?.base ?? h, cell: marks.cell, behind: marks.behind, front: marks.front };
    cache.set(frame, found);
    return found;
  };
}

/**
 * The placed props of a map that a client draws as props, with the depth each draws at: those without a behaviour
 * and those driven by presence, each in the layer it stands in, keyed over its whole layer as the renderer keys it.
 *
 * @param map The map.
 * @param context The compile context, for the frames' sizes.
 * @param appearances The world's appearance table.
 * @param keys Each prop's tie key.
 * @param tileSize Tile size in pixels.
 * @returns The placements, in layer then prop order.
 */
function standPlacements(map: MapDocument, context: CompileContext, appearances: AppearanceTable, keys: ReadonlyMap<PropInstance, number>, tileSize: number): StandPlacement[] {
  const frameOf = withBareFrames(framesOf(context), context);
  const band = playersAbove(map);
  const placements: StandPlacement[] = [];
  map.layers.forEach((layer, index) => {
    if (layer.kind !== "prop" || index < band) {
      return;
    }
    for (const prop of layer.props) {
      if (prop.behaviour !== undefined && presenceIndex(prop, appearances) === undefined && !holdsStill(context.objectTypes.find((t) => t.type === prop.behaviour?.type))) {
        continue;
      }
      const id = frameId(prop.frame.tileset, null, prop.frame.tile);
      const frame = frameOf(id);
      if (frame === undefined) {
        continue;
      }
      const depth = standPropDepth({ x: prop.x, y: prop.y, flipY: prop.flipY, sortPixels: prop.sortPixels, order: keys.get(prop) ?? 0 }, frame, tileSize, layerDepth(index, band), map.height);
      const own = ownMarksOf(prop.footprint);
      placements.push({ frame: id, x: prop.x, y: prop.y, flipX: prop.flipX, flipY: prop.flipY, sortPixels: prop.sortPixels, depth, ...(own === undefined ? {} : { own }) });
    }
  });
  return placements;
}

/**
 * A map's stand table (ADR-132 decision 5): `standMarks` over the map's props in the Players band, whose marks the
 * compile slices into each client chunk. A prop's own Behind and In front cells replace its frame's (decision 12).
 * A map none of whose props is painted gives an empty table.
 *
 * @param map The map.
 * @param context The compile context, which carries the painted frames.
 * @param appearances The world's appearance table.
 * @param keys Each prop's tie key.
 * @param tileSize Tile size in pixels.
 * @returns The table.
 */
export function mapStandTable(map: MapDocument, context: CompileContext, appearances: AppearanceTable, keys: ReadonlyMap<PropInstance, number>, tileSize: number): StandTable {
  const placements = standPlacements(map, context, appearances, keys, tileSize);
  const framesPainted = context.standMarks !== undefined && Object.keys(context.standMarks).length > 0;
  if (!framesPainted && !placements.some((placement) => placement.own !== undefined)) {
    return { width: 0, height: 0, keys: new Int32Array(0), sides: new Uint8Array(0), depths: new Float64Array(0) };
  }
  return standMarks(placements, withBareFrames(framesOf(context), context), tileSize, map.width, map.height).table;
}
